import BoltIcon from '@mui/icons-material/Bolt';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import {
  useDepletionsKpis,
  useDepletionsTrend,
  useFollowUpTracker,
  useVelocityAnalysis,
} from '../api/depletions';
import { colors } from '../theme';
import { SectionExplainer } from './SectionExplainer';

// Time horizon for the projection. Sliders give a delta; the horizon
// tells you "over what period" — without it, projected impact is
// dimensionless. 1 month / 3 months / 12 months matches how reps
// already think about goals (month-end, quarter, annual quota).
type HorizonMonths = 1 | 3 | 12;

const HORIZONS: { value: HorizonMonths; label: string; desc: string }[] = [
  { value: 1, label: '30 days', desc: 'Next month' },
  { value: 3, label: '90 days', desc: 'This quarter' },
  { value: 12, label: 'Full year', desc: 'Next 12 months' },
];

// Citation breakdown for each driver. Mirrors the ActionCell pattern
// so the rep can audit how every projected number was assembled.
interface DriverCitation {
  source: string;
  formula: string;
}

interface Params {
  reactivation: number;
  newAccounts: number;
  frequencyShift: number;
  productPenetration: number;
}

const DEFAULTS: Params = {
  reactivation: 0,
  newAccounts: 0,
  frequencyShift: 0,
  productPenetration: 0,
};

interface Preset {
  label: string;
  desc: string;
  params: Params;
}

const PRESETS: Preset[] = [
  {
    label: 'Reactivation Push',
    desc: 'Win back 20% of at-risk, +10% frequency',
    params: { reactivation: 20, newAccounts: 0, frequencyShift: 10, productPenetration: 0 },
  },
  {
    label: 'Growth Blitz',
    desc: '50 new accounts, +2 SKUs/account',
    params: { reactivation: 0, newAccounts: 50, frequencyShift: 0, productPenetration: 2 },
  },
  {
    label: 'Full Throttle',
    desc: 'All levers up',
    params: { reactivation: 15, newAccounts: 30, frequencyShift: 15, productPenetration: 1 },
  },
  {
    label: 'Retention Focus',
    desc: '30% reactivation, +20% frequency',
    params: { reactivation: 30, newAccounts: 0, frequencyShift: 20, productPenetration: 0 },
  },
];

function fmtVol(v: number): string {
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toFixed(1);
}

// 6-pack equivalent — 1 9L case = 2 6-packs. Same K-compression as
// fmtVol so headline numbers stay aligned.
function fmt6Pk(v: number): string {
  const sixPk = v * 2;
  if (Math.abs(sixPk) >= 1000) return `${(sixPk / 1000).toFixed(1)}K`;
  return Math.round(sixPk).toString();
}

const wPct = (v: number): string => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

export function DepletionsWhatIfSimulator() {
  const kpisQ = useDepletionsKpis();
  const trackerQ = useFollowUpTracker();
  const velocityQ = useVelocityAnalysis();
  const trendQ = useDepletionsTrend();
  const [params, setParams] = useState<Params>(DEFAULTS);
  const [horizonMonths, setHorizonMonths] = useState<HorizonMonths>(3);
  const [targetInput, setTargetInput] = useState<string>('');

  const isLoading =
    kpisQ.isLoading || trackerQ.isLoading || velocityQ.isLoading || trendQ.isLoading;
  const hasChanges =
    params.reactivation !== 0 ||
    params.newAccounts !== 0 ||
    params.frequencyShift !== 0 ||
    params.productPenetration !== 0;

  const update = <K extends keyof Params>(key: K, val: Params[K]) =>
    setParams((p) => ({ ...p, [key]: val }));

  // Derived baseline metrics from the live API data.
  const derived = useMemo(() => {
    if (!kpisQ.data || !trackerQ.data || !velocityQ.data || !trendQ.data) return null;

    const totalVolume = Number(kpisQ.data.total_9l);
    const totalAccounts = kpisQ.data.unique_accounts;

    // Recent monthly run-rate — last 3 months of the trend line.
    // This is the anchor for "do nothing" projections; the full-period
    // average would over-weight old months that no longer reflect the
    // current pace of the business.
    const points = trendQ.data.points;
    const lifetimeMonths = points.length || 1;
    const recentPoints = points.slice(-3);
    const monthlyRate =
      recentPoints.length > 0
        ? recentPoints.reduce((s, p) => s + Number(p.cases_9l), 0) / recentPoints.length
        : totalVolume / lifetimeMonths;

    // Per-account monthly volume — used to scale lever impacts to a
    // chosen time horizon instead of treating them as one-shot adds.
    const monthlyVolPerAccount = totalAccounts > 0 ? monthlyRate / totalAccounts : 0;

    const atRiskBucket = trackerQ.data.buckets.find((b) => b.label === '120+ days');
    const atRiskCount = atRiskBucket?.count ?? 0;
    // At-risk accounts' historical per-month pace when they WERE active.
    const atRiskMonthlyVol =
      atRiskBucket && atRiskBucket.accounts.length > 0
        ? atRiskBucket.accounts.reduce((s, a) => s + Number(a.total_9l), 0) /
          atRiskBucket.accounts.length /
          lifetimeMonths
        : monthlyVolPerAccount * 0.4;

    // "Active in last 90 days" → 0-30 + 30-60 + 60-90 buckets summed.
    const activeAccounts = trackerQ.data.buckets
      .filter((b) => ['0-30 days', '30-60 days', '60-90 days'].includes(b.label))
      .reduce((s, b) => s + b.count, 0);

    // Infrequent buyers = velocity categories that reorder rarely.
    const infrequent = velocityQ.data.accounts.filter(
      (a) => a.category === 'declining' || a.category === 'silent',
    );
    const infrequentCount = infrequent.length;
    const infrequentMonthlyVol =
      infrequentCount > 0
        ? infrequent.reduce((s, a) => s + Number(a.total_9l), 0) / infrequentCount / lifetimeMonths
        : 0;

    return {
      totalAccounts,
      monthlyRate,
      monthlyVolPerAccount,
      atRiskCount,
      atRiskMonthlyVol,
      activeAccounts,
      infrequentCount,
      infrequentMonthlyVol,
    };
  }, [kpisQ.data, trackerQ.data, velocityQ.data, trendQ.data]);

  // Simulated projection — baseline (do nothing) + each lever's
  // contribution scaled to the chosen horizon. Ramp multipliers
  // (0.5 / 0.6 / 0.3) preserve the original conservative assumptions
  // about how much of the theoretical lift actually lands in-window.
  const sim = useMemo(() => {
    if (!derived) return null;
    const {
      totalAccounts,
      monthlyRate,
      monthlyVolPerAccount,
      atRiskCount,
      atRiskMonthlyVol,
      infrequentCount,
      infrequentMonthlyVol,
      activeAccounts,
    } = derived;

    const m = horizonMonths;

    // Baseline = "do nothing" — keep the current monthly pace for m months.
    const baselineVolume = monthlyRate * m;

    // Reactivation: reactivated accounts buy at their historical pace,
    // but ramped at 50% to reflect that warm-back accounts don't
    // instantly resume full volume.
    const reactivatedAccounts = Math.round((atRiskCount * params.reactivation) / 100);
    const reactivationVolume = reactivatedAccounts * atRiskMonthlyVol * m * 0.5;

    // New accounts: assume they ramp to 60% of avg monthly volume.
    const newAccountVolume = params.newAccounts * monthlyVolPerAccount * m * 0.6;

    // Frequency: converted infrequent buyers get an extra 30% of their
    // baseline pace each month over the horizon.
    const frequencyAccounts = Math.round((infrequentCount * params.frequencyShift) / 100);
    const frequencyVolume = frequencyAccounts * infrequentMonthlyVol * m * 0.3;

    // Product penetration: each added SKU lifts the baseline by ~4%.
    const penetrationVolume = baselineVolume * (params.productPenetration * 0.04);

    const adjustedVolume =
      baselineVolume + reactivationVolume + newAccountVolume + frequencyVolume + penetrationVolume;

    const projectedAccounts = totalAccounts + params.newAccounts + reactivatedAccounts;
    const projectedAtRisk = Math.max(0, atRiskCount - reactivatedAccounts);
    const projectedActiveRate =
      projectedAccounts > 0
        ? ((activeAccounts + reactivatedAccounts) / projectedAccounts) * 100
        : 0;
    const currentActiveRate = totalAccounts > 0 ? (activeAccounts / totalAccounts) * 100 : 0;

    return {
      baselineVolume,
      adjustedVolume,
      projectedAccounts,
      projectedAtRisk,
      projectedActiveRate,
      currentActiveRate,
      reactivatedAccounts,
      reactivationVolume,
      newAccountVolume,
      frequencyAccounts,
      frequencyVolume,
      penetrationVolume,
    };
  }, [derived, params, horizonMonths]);

  // Parsed target — empty string clears it; non-numeric falls through to null.
  const targetVolume = useMemo(() => {
    const cleaned = targetInput.replace(/[^0-9.]/g, '');
    const parsed = Number(cleaned);
    // The user enters a 6-pack goal; convert to the 9L scale used
    // internally (1 case = 2 six-packs) so it compares against the
    // 9L baseline/adjusted volumes. Display helpers re-render it as 6-pk.
    return cleaned && !Number.isNaN(parsed) && parsed > 0 ? parsed / 2 : null;
  }, [targetInput]);

  if (isLoading || !derived || !sim) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          What-If Simulator
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  // Delta is now lift ABOVE the do-nothing baseline (the run-rate
  // projection over the chosen horizon). This is the apples-to-apples
  // "what your scenario actually adds" — the old version compared a
  // horizon projection to cumulative total, which was misleading.
  const volDelta = sim.adjustedVolume - sim.baselineVolume;
  const volDeltaPct = sim.baselineVolume > 0 ? (volDelta / sim.baselineVolume) * 100 : 0;

  const currentHorizon = HORIZONS.find((h) => h.value === horizonMonths)!;
  const targetGapPct = targetVolume
    ? Math.min(100, (sim.adjustedVolume / targetVolume) * 100)
    : null;
  const targetBaselineGapPct = targetVolume
    ? Math.min(100, (sim.baselineVolume / targetVolume) * 100)
    : null;

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${colors.success}14`,
              flexShrink: 0,
            }}
          >
            <TuneOutlinedIcon sx={{ color: colors.success, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 17,
                color: colors.textPrimary,
                lineHeight: 1.25,
                letterSpacing: '-0.01em',
              }}
            >
              What-If Simulator
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.textMuted, mt: 0.25 }}>
              Project reactivation, growth, and frequency scenarios against your run-rate baseline.
            </Typography>
          </Box>
        </Box>
        <Button
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={() => setParams(DEFAULTS)}
          disabled={!hasChanges}
          sx={{
            color: colors.textMuted,
            textTransform: 'none',
            '&:hover': { color: colors.success },
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Horizon + target — sets the period over which the projection
          is computed and an optional goal to measure against. These
          frame everything below: same sliders read very differently
          at 30 days vs full year. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          mb: 2.5,
          px: 2,
          py: 1.25,
          bgcolor: colors.surfaceSubtle,
          border: `1px solid ${colors.borderLight}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Horizon
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {HORIZONS.map((h) => {
              const active = horizonMonths === h.value;
              return (
                <Tooltip key={h.value} title={h.desc} arrow placement="top">
                  <Chip
                    label={h.label}
                    onClick={() => setHorizonMonths(h.value)}
                    size="small"
                    sx={{
                      height: 26,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: active ? `${colors.success}1f` : '#fff',
                      color: active ? colors.success : colors.textSecondary,
                      border: `1px solid ${active ? `${colors.success}55` : colors.borderLight}`,
                      '&:hover': {
                        bgcolor: active ? `${colors.success}26` : colors.surfaceHover,
                      },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted, ml: 0.5 }}>
            Baseline ≈ {fmt6Pk(sim.baselineVolume)} 6-pk · {fmtVol(sim.baselineVolume)} 9L (recent
            run-rate × {horizonMonths} mo)
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Target
          </Typography>
          <TextField
            size="small"
            placeholder="e.g. 5000"
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            sx={{
              width: 150,
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                fontSize: 12.5,
                height: 30,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <FlagOutlinedIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: 11, color: colors.textMuted }}>6-pk</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Presets */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 11.5,
            color: colors.textMuted,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mb: 1,
          }}
        >
          Quick Scenarios
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              icon={<BoltIcon sx={{ fontSize: 14 }} />}
              label={preset.label}
              onClick={() => setParams(preset.params)}
              sx={{
                height: 32,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                bgcolor: colors.surfaceSubtle,
                color: colors.textSecondary,
                border: `1px solid ${colors.borderLight}`,
                '&:hover': {
                  bgcolor: `${colors.success}0d`,
                  borderColor: `${colors.success}40`,
                  color: colors.success,
                },
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Controls + Results */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' },
          gap: 3,
          alignItems: 'flex-start',
        }}
      >
        {/* Controls */}
        <Box
          sx={{
            bgcolor: colors.surfaceSubtle,
            borderRadius: 2.5,
            border: `1px solid ${colors.borderLight}`,
            p: 2.5,
          }}
        >
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, fontWeight: 600, mb: 2 }}>
            Adjust Parameters
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Reactivation */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}>
                  Account Reactivation
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: params.reactivation !== 0 ? colors.success : colors.textMuted,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {params.reactivation}%
                </Typography>
              </Box>
              <Slider
                value={params.reactivation}
                onChange={(_, v) => update('reactivation', Array.isArray(v) ? (v[0] ?? 0) : v)}
                min={0}
                max={50}
                step={5}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 25, label: '25%' },
                  { value: 50, label: '50%' },
                ]}
                sx={{
                  color: colors.success,
                  '& .MuiSlider-markLabel': { color: colors.textMuted, fontSize: 11 },
                }}
              />
              <Typography sx={{ fontSize: 11.5, color: colors.textMuted, mt: -0.5 }}>
                Win back{' '}
                {params.reactivation > 0
                  ? Math.round((derived.atRiskCount * params.reactivation) / 100)
                  : '—'}{' '}
                of {derived.atRiskCount} at-risk accounts (120+ days dormant)
              </Typography>
            </Box>

            {/* New Accounts */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}>
                  New Account Growth
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: params.newAccounts > 0 ? colors.info : colors.textMuted,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  +{params.newAccounts}
                </Typography>
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  value={params.newAccounts}
                  onChange={(e) => update('newAccounts', Number(e.target.value))}
                >
                  <MenuItem value={0}>No new accounts</MenuItem>
                  {[10, 20, 30, 50, 75, 100].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} new accounts (+
                      {fmt6Pk(n * derived.monthlyVolPerAccount * horizonMonths * 0.6)} 6-pk est.)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: 11.5, color: colors.textMuted, mt: 0.5 }}>
                Each new account ≈ {fmt6Pk(derived.monthlyVolPerAccount * horizonMonths * 0.6)} 6-pk
                over {currentHorizon.label.toLowerCase()} (60% of avg pace during ramp-up)
              </Typography>
            </Box>

            {/* Frequency */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}>
                  Frequency Improvement
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: params.frequencyShift !== 0 ? colors.warning : colors.textMuted,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {wPct(params.frequencyShift)}
                </Typography>
              </Box>
              <Slider
                value={params.frequencyShift}
                onChange={(_, v) => update('frequencyShift', Array.isArray(v) ? (v[0] ?? 0) : v)}
                min={0}
                max={30}
                step={5}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 15, label: '15%' },
                  { value: 30, label: '30%' },
                ]}
                sx={{
                  color: colors.warning,
                  '& .MuiSlider-markLabel': { color: colors.textMuted, fontSize: 11 },
                }}
              />
              <Typography sx={{ fontSize: 11.5, color: colors.textMuted, mt: -0.5 }}>
                Convert{' '}
                {params.frequencyShift > 0
                  ? Math.round((derived.infrequentCount * params.frequencyShift) / 100)
                  : '—'}{' '}
                of {derived.infrequentCount} declining/silent buyers to order more often
              </Typography>
            </Box>

            {/* Product Penetration */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}>
                  Product Penetration
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: params.productPenetration !== 0 ? '#8b5cf6' : colors.textMuted,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  +{params.productPenetration} SKU{params.productPenetration !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Slider
                value={params.productPenetration}
                onChange={(_, v) =>
                  update('productPenetration', Array.isArray(v) ? (v[0] ?? 0) : v)
                }
                min={0}
                max={3}
                step={1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 1, label: '+1' },
                  { value: 2, label: '+2' },
                  { value: 3, label: '+3' },
                ]}
                sx={{
                  color: '#8b5cf6',
                  '& .MuiSlider-markLabel': { color: colors.textMuted, fontSize: 11 },
                }}
              />
              <Typography sx={{ fontSize: 11.5, color: colors.textMuted, mt: -0.5 }}>
                Add SKUs per account — each new SKU adds ~4% volume uplift
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Results */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              bgcolor: hasChanges
                ? volDelta >= 0
                  ? `${colors.success}0a`
                  : `${colors.error}0a`
                : colors.surfaceSubtle,
              border: `1px solid ${
                hasChanges
                  ? volDelta >= 0
                    ? `${colors.success}33`
                    : `${colors.error}33`
                  : colors.borderLight
              }`,
              borderRadius: 2.5,
              p: 2.5,
            }}
          >
            {!hasChanges ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <TuneOutlinedIcon
                  sx={{ fontSize: 32, color: colors.textMuted, mb: 1, opacity: 0.4 }}
                />
                <Typography sx={{ fontSize: 14, color: colors.textMuted }}>
                  Adjust the sliders or pick a scenario to see projected impact.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {volDelta >= 0 ? (
                    <TrendingUpIcon sx={{ color: colors.success, fontSize: 20 }} />
                  ) : (
                    <TrendingDownIcon sx={{ color: colors.error, fontSize: 20 }} />
                  )}
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                    Projected Impact
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1.5,
                    mb: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      sx={{
                        fontSize: 32,
                        fontWeight: 800,
                        color: volDelta >= 0 ? colors.success : colors.error,
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {volDelta >= 0 ? '+' : ''}
                      {fmt6Pk(volDelta)} 6-pk
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: colors.textMuted,
                        fontFeatureSettings: '"tnum"',
                        mt: 0.25,
                      }}
                    >
                      {volDelta >= 0 ? '+' : ''}
                      {fmt6Pk(volDelta)} 6-pk
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: volDelta >= 0 ? colors.success : colors.error,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    ({wPct(volDeltaPct)})
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                    over {currentHorizon.label.toLowerCase()} vs do-nothing baseline
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    rowGap: 0.5,
                    mb: 2,
                    fontSize: 12,
                    color: colors.textMuted,
                  }}
                >
                  <Box>
                    Do nothing:{' '}
                    <Box
                      component="span"
                      sx={{
                        color: colors.textSecondary,
                        fontWeight: 600,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {fmt6Pk(sim.baselineVolume)} 6-pk · {fmtVol(sim.baselineVolume)} 9L
                    </Box>
                  </Box>
                  <Box>
                    Adjusted:{' '}
                    <Box
                      component="span"
                      sx={{
                        color: volDelta >= 0 ? colors.success : colors.error,
                        fontWeight: 700,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {fmt6Pk(sim.adjustedVolume)} 6-pk · {fmtVol(sim.adjustedVolume)} 9L
                    </Box>
                  </Box>
                </Box>

                {/* Target gap bar — only shown when the user has typed a goal. */}
                {targetVolume && targetGapPct !== null && targetBaselineGapPct !== null && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        columnGap: 1.5,
                        rowGap: 0.25,
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
                        Target: {fmt6Pk(targetVolume)} 6-pk · {fmtVol(targetVolume)} 9L for{' '}
                        {currentHorizon.label.toLowerCase()}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color:
                            sim.adjustedVolume >= targetVolume
                              ? colors.success
                              : volDelta > 0
                                ? colors.warning
                                : colors.error,
                          fontFeatureSettings: '"tnum"',
                        }}
                      >
                        {sim.adjustedVolume >= targetVolume
                          ? `+${fmt6Pk(sim.adjustedVolume - targetVolume)} 6-pk · +${fmtVol(sim.adjustedVolume - targetVolume)} 9L over target`
                          : `${fmt6Pk(targetVolume - sim.adjustedVolume)} 6-pk · ${fmtVol(targetVolume - sim.adjustedVolume)} 9L short`}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        position: 'relative',
                        height: 8,
                        borderRadius: 4,
                        bgcolor: colors.surfaceHover,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Baseline progress (lighter) */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${targetBaselineGapPct}%`,
                          bgcolor: `${colors.textMuted}66`,
                        }}
                      />
                      {/* Adjusted progress (full) */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${targetGapPct}%`,
                          bgcolor:
                            sim.adjustedVolume >= targetVolume ? colors.success : colors.warning,
                          transition: 'width 0.25s ease',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        columnGap: 1,
                        rowGap: 0.25,
                        mt: 0.4,
                        fontSize: 10,
                        color: colors.textMuted,
                      }}
                    >
                      <Box component="span">0</Box>
                      <Box component="span" sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
                        Baseline {targetBaselineGapPct.toFixed(0)}% · Adjusted{' '}
                        {targetGapPct.toFixed(0)}%
                      </Box>
                      <Box component="span">{fmt6Pk(targetVolume)}</Box>
                    </Box>
                  </Box>
                )}

                {/* Metric grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                    gap: 1.5,
                  }}
                >
                  <MetricCard
                    label={`Volume (${currentHorizon.label})`}
                    current={`${fmt6Pk(sim.baselineVolume)} 6-pk · ${fmtVol(sim.baselineVolume)} 9L`}
                    projected={`${fmt6Pk(sim.adjustedVolume)} 6-pk · ${fmtVol(sim.adjustedVolume)} 9L`}
                    delta={volDelta}
                    deltaText={`${volDelta >= 0 ? '+' : ''}${fmt6Pk(volDelta)} 6-pk · ${volDelta >= 0 ? '+' : ''}${fmtVol(volDelta)} 9L`}
                    color={colors.success}
                    positive={volDelta >= 0}
                  />
                  <MetricCard
                    label="Accounts"
                    current={derived.totalAccounts.toLocaleString()}
                    projected={sim.projectedAccounts.toLocaleString()}
                    delta={sim.projectedAccounts - derived.totalAccounts}
                    deltaText={`+${sim.projectedAccounts - derived.totalAccounts}`}
                    color={colors.info}
                    positive
                  />
                  <MetricCard
                    label="At Risk"
                    current={derived.atRiskCount.toLocaleString()}
                    projected={sim.projectedAtRisk.toLocaleString()}
                    delta={sim.projectedAtRisk - derived.atRiskCount}
                    deltaText={`${sim.projectedAtRisk - derived.atRiskCount}`}
                    color={colors.error}
                    positive={sim.projectedAtRisk - derived.atRiskCount <= 0}
                  />
                  <MetricCard
                    label="Active Rate"
                    current={`${sim.currentActiveRate.toFixed(1)}%`}
                    projected={`${sim.projectedActiveRate.toFixed(1)}%`}
                    delta={sim.projectedActiveRate - sim.currentActiveRate}
                    deltaText={`${sim.projectedActiveRate - sim.currentActiveRate >= 0 ? '+' : ''}${(
                      sim.projectedActiveRate - sim.currentActiveRate
                    ).toFixed(1)}pp`}
                    color="#8b5cf6"
                    positive={sim.projectedActiveRate - sim.currentActiveRate >= 0}
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Drivers */}
          {hasChanges && (
            <Box
              sx={{
                bgcolor: colors.surfaceSubtle,
                borderRadius: 2.5,
                border: `1px solid ${colors.borderLight}`,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 13, color: colors.textSecondary, fontWeight: 600 }}>
                  What's driving this
                </Typography>
                <Typography sx={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
                  Hover any row for the math
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  params.reactivation > 0 && {
                    label: `Reactivate ${sim.reactivatedAccounts} at-risk accounts (${params.reactivation}%)`,
                    impact: sim.reactivationVolume,
                    color: colors.success,
                    citation: {
                      source: `At-risk bucket (120+ days dormant) · trend run-rate`,
                      formula: `${sim.reactivatedAccounts} reactivated × ${fmt6Pk(derived.atRiskMonthlyVol)} 6-pk/mo (their historical pace) × ${horizonMonths} mo × 0.5 ramp factor = ${fmt6Pk(sim.reactivationVolume)} 6-pk`,
                    } satisfies DriverCitation,
                  },
                  params.newAccounts > 0 && {
                    label: `Add ${params.newAccounts} new accounts`,
                    impact: sim.newAccountVolume,
                    color: colors.info,
                    citation: {
                      source: `Avg monthly volume per account · trend run-rate`,
                      formula: `${params.newAccounts} new × ${fmt6Pk(derived.monthlyVolPerAccount)} 6-pk/mo (company avg) × ${horizonMonths} mo × 0.6 ramp factor = ${fmt6Pk(sim.newAccountVolume)} 6-pk`,
                    } satisfies DriverCitation,
                  },
                  params.frequencyShift > 0 && {
                    label: `Convert ${sim.frequencyAccounts} buyers to higher frequency (${params.frequencyShift}%)`,
                    impact: sim.frequencyVolume,
                    color: colors.warning,
                    citation: {
                      source: `Velocity analysis · declining + silent accounts`,
                      formula: `${sim.frequencyAccounts} converted × ${fmt6Pk(derived.infrequentMonthlyVol)} 6-pk/mo (their pace) × ${horizonMonths} mo × 0.3 frequency lift = ${fmt6Pk(sim.frequencyVolume)} 6-pk`,
                    } satisfies DriverCitation,
                  },
                  params.productPenetration > 0 && {
                    label: `Add ${params.productPenetration} SKU${
                      params.productPenetration > 1 ? 's' : ''
                    } per account (cross-sell)`,
                    impact: sim.penetrationVolume,
                    color: '#8b5cf6',
                    citation: {
                      source: `Baseline projection × per-SKU uplift assumption`,
                      formula: `${fmt6Pk(sim.baselineVolume)} 6-pk baseline × ${params.productPenetration} SKU × 4% per-SKU uplift = ${fmt6Pk(sim.penetrationVolume)} 6-pk`,
                    } satisfies DriverCitation,
                  },
                ]
                  .filter(
                    (
                      d,
                    ): d is {
                      label: string;
                      impact: number;
                      color: string;
                      citation: DriverCitation;
                    } => Boolean(d),
                  )
                  .map((driver) => (
                    <Tooltip
                      key={driver.label}
                      placement="left"
                      arrow
                      title={
                        <Box sx={{ p: 0.25, maxWidth: 320 }}>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 700,
                              color: driver.color,
                              mb: 0.4,
                            }}
                          >
                            How this number was built
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              fontWeight: 700,
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              color: colors.textMuted,
                            }}
                          >
                            Source
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: colors.textSecondary,
                              fontStyle: 'italic',
                              mb: 0.6,
                            }}
                          >
                            {driver.citation.source}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              fontWeight: 700,
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              color: colors.textMuted,
                            }}
                          >
                            Formula
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: colors.textSecondary,
                              lineHeight: 1.5,
                              fontFeatureSettings: '"tnum"',
                            }}
                          >
                            {driver.citation.formula}
                          </Typography>
                        </Box>
                      }
                      slotProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#fff',
                            color: colors.textPrimary,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 1.25,
                            boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
                            maxWidth: 360,
                          },
                        },
                        arrow: {
                          sx: {
                            color: '#fff',
                            '&::before': { border: `1px solid ${colors.border}` },
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          cursor: 'help',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 1,
                          '&:hover': { bgcolor: colors.surfaceHover },
                          transition: 'background-color 0.12s',
                        }}
                      >
                        <Box
                          sx={{
                            width: 4,
                            height: 24,
                            borderRadius: 2,
                            bgcolor: driver.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography sx={{ fontSize: 13, color: colors.textSecondary, flex: 1 }}>
                          {driver.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: colors.success,
                            fontFeatureSettings: '"tnum"',
                          }}
                        >
                          +{fmt6Pk(driver.impact)} 6-pk · +{fmtVol(driver.impact)} 9L
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <SectionExplainer
        rows={[
          {
            sample: <WISampleHorizonChip />,
            term: 'Horizon toggle (30 days · 90 days · Full year)',
            desc: (
              <>
                Sets the period the projection runs over. The same sliders produce very different
                numbers at 30 days vs full year.
              </>
            ),
          },
          {
            sample: <WISampleBaselineChip />,
            term: 'Baseline (do-nothing run-rate)',
            desc: (
              <>
                Recent-3-months monthly volume × horizon months. The "if nothing changes" projection
                — every scenario reads as a<strong> delta above this baseline</strong>.
              </>
            ),
          },
          {
            sample: <WISampleSlider />,
            term: 'Parameter sliders',
            desc: (
              <>
                <strong>Reactivation %</strong>, <strong>New accounts</strong>,
                <strong>Frequency %</strong>, <strong>Product penetration</strong>. Each lever's
                contribution is scaled to the horizon and ramp-discounted (50% / 60% / 30%) to stay
                conservative.
              </>
            ),
          },
          {
            sample: <WISampleGapBar />,
            term: 'Target gap bar',
            desc: (
              <>
                Enter a 6-pk goal for the horizon. The bar visualizes how close the baseline
                (lighter) and adjusted (full color) projections land vs the target. Tells you "how
                much short / over" in one read.
              </>
            ),
          },
          {
            sample: <WISampleCitation />,
            term: 'Driver rows in "What\'s driving this"',
            desc: (
              <>
                Per-lever volume contributions. Hover any row for the
                <strong> citation tooltip</strong> — source, formula, and the conservative ramp
                multiplier so you can audit how each number was assembled.
              </>
            ),
          },
        ]}
      />
    </Paper>
  );
}

interface MetricCardProps {
  label: string;
  current: string;
  projected: string;
  delta: number;
  deltaText: string;
  color: string;
  positive: boolean;
}

function MetricCard({
  label,
  current,
  projected,
  delta,
  deltaText,
  color,
  positive,
}: MetricCardProps) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: 2,
        border: `1px solid ${colors.borderLight}`,
        p: 1.5,
        '&:hover': { borderColor: `${color}55` },
        transition: 'border-color 0.15s',
      }}
    >
      <Typography
        sx={{
          fontSize: 10.5,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
        {current}
      </Typography>
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color,
          lineHeight: 1.25,
          letterSpacing: '-0.01em',
        }}
      >
        {projected}
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          color: delta === 0 ? colors.textMuted : positive ? colors.success : colors.error,
          fontFeatureSettings: '"tnum"',
        }}
      >
        {deltaText}
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------
// What-If sample swatches
// ----------------------------------------------------------------

function WISampleHorizonChip() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        gap: 0,
        bgcolor: colors.surfaceSubtle,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 1,
        p: 0.2,
        fontSize: 9,
        fontWeight: 700,
      }}
    >
      <Box
        component="span"
        sx={{ px: 0.5, py: 0.1, bgcolor: '#fff', borderRadius: 0.75, color: colors.success }}
      >
        90d
      </Box>
      <Box component="span" sx={{ px: 0.5, py: 0.1, color: colors.textMuted }}>
        1y
      </Box>
    </Box>
  );
}

function WISampleBaselineChip() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 700,
        color: colors.textSecondary,
        bgcolor: colors.surfaceHover,
        border: `1px solid ${colors.borderLight}`,
        px: 0.6,
        py: 0.15,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
        fontFeatureSettings: '"tnum"',
      }}
    >
      Run-rate
    </Box>
  );
}

function WISampleSlider() {
  return (
    <Box
      sx={{
        width: 56,
        height: 4,
        bgcolor: colors.borderLight,
        borderRadius: 2,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: '40%',
          bgcolor: colors.success,
          borderRadius: 2,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: '40%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: '#fff',
          border: `2px solid ${colors.success}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        }}
      />
    </Box>
  );
}

function WISampleGapBar() {
  return (
    <Box
      sx={{
        width: 56,
        height: 6,
        bgcolor: colors.borderLight,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: '55%',
          bgcolor: `${colors.textMuted}66`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: '78%',
          bgcolor: colors.warning,
        }}
      />
    </Box>
  );
}

function WISampleCitation() {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.3,
        fontSize: 9,
        fontWeight: 700,
        color: colors.textSecondary,
        bgcolor: colors.surfaceSubtle,
        border: `1px dashed ${colors.border}`,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.5,
      }}
    >
      <Box component="span" sx={{ color: colors.chartPrimary, fontWeight: 800 }}>
        ƒ(x)
      </Box>
      <Box component="span">+24</Box>
    </Box>
  );
}
